import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import JSZip from 'jszip'; // npm install jszip
import { saveAs } from 'file-saver'; // npm install file-saver
import { notificar } from '../utils/notify';
import { formatarCNPJ } from '../utils/formatters';
import '../styles/RelatorioFiscal.css';

const RelatorioFiscal = () => {
  // Função para limpar CNPJ (remover formatação)
  const limparCNPJ = (cnpj) => {
    return cnpj ? cnpj.replace(/\D/g, '') : '';
  };

  const [empresas, setEmpresas] = useState([]);
  const [selecionadas, setSelecionadas] = useState([]);
  const [mensagem, setMensagem] = useState('');

  useEffect(() => { notificar(mensagem); }, [mensagem]);
  const [carregando, setCarregando] = useState(true);
  const [gerando, setGerando] = useState(false);
  const [progresso, setProgresso] = useState('');
  const [relatoriosGerados, setRelatoriosGerados] = useState([]);
  const [empresasComErro, setEmpresasComErro] = useState([]);

  // Estados para o modal de adicionar empresa
  const [showModal, setShowModal] = useState(false);
  const [novaEmpresa, setNovaEmpresa] = useState({
    razaosocial: '',
    cnpj: ''
  });
  const [salvandoEmpresa, setSalvandoEmpresa] = useState(false);

  // Estados para confirmação de delete
  const [empresaParaDeletar, setEmpresaParaDeletar] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletandoEmpresa, setDeletandoEmpresa] = useState(false);

  const [filtros, setFiltros] = useState({
    razaosocial: '',
    cnpj: ''
  });

  // Função para carregar empresas
  const carregarEmpresas = useCallback(() => {
    setCarregando(true);
    setMensagem('');

    const accessToken = localStorage.getItem('access');
    
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/empresas-das/`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      })
      .then((res) => {
        if (Array.isArray(res.data)) {
          setEmpresas(res.data);
        } else {
          console.error('Dados recebidos não são um array:', res.data);
          setMensagem('Formato de dados inválido');
        }
        setCarregando(false);
      })
      .catch((error) => {
        console.error('Erro ao carregar empresas:', error);
        const errorMsg = error.response?.data?.erro || 
                         error.response?.data?.mensagem || 
                         error.message || 
                         'Erro desconhecido ao carregar empresas';
        setMensagem(`Erro: ${errorMsg}`);
        setCarregando(false);
      });
  }, []);

  useEffect(() => {
    carregarEmpresas();
  }, [carregarEmpresas]);

  // ================================
  // FUNÇÕES DE CRUD
  // ================================

  // Adicionar nova empresa
  const adicionarEmpresa = async () => {
    if (!novaEmpresa.razaosocial.trim() || !novaEmpresa.cnpj.trim()) {
      setMensagem('Razão social e CNPJ são obrigatórios');
      return;
    }

    const cnpjLimpo = limparCNPJ(novaEmpresa.cnpj);
    if (cnpjLimpo.length !== 14) {
      setMensagem('CNPJ deve ter 14 dígitos');
      return;
    }

    setSalvandoEmpresa(true);
    const accessToken = localStorage.getItem('access');

    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/empresas/adicionar/`,
        {
          razaosocial: novaEmpresa.razaosocial.trim(),
          cnpj: cnpjLimpo
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          }
        }
      );

      setMensagem('Empresa adicionada com sucesso!');
      setShowModal(false);
      setNovaEmpresa({ razaosocial: '', cnpj: '' });
      carregarEmpresas(); // Recarregar lista
    } catch (error) {
      console.error('Erro ao adicionar empresa:', error);
      const errorMsg = error.response?.data?.erro || 
                       error.response?.data?.mensagem || 
                       error.message || 
                       'Erro desconhecido ao adicionar empresa';
      setMensagem(`Erro ao adicionar empresa: ${errorMsg}`);
    } finally {
      setSalvandoEmpresa(false);
    }
  };

  // Deletar empresa
  const deletarEmpresa = async (cnpj, razaosocial) => {
    setEmpresaParaDeletar({ cnpj, razaosocial });
    setShowDeleteModal(true);
  };

  const confirmarDelete = async () => {
    if (!empresaParaDeletar) return;

    setDeletandoEmpresa(true);
    const accessToken = localStorage.getItem('access');
    const cnpjLimpo = limparCNPJ(empresaParaDeletar.cnpj);

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE}/api/empresas/${cnpjLimpo}/`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        }
      );

      setMensagem(`Empresa ${empresaParaDeletar.razaosocial} deletada com sucesso!`);
      setShowDeleteModal(false);
      setEmpresaParaDeletar(null);
      
      // Remover da lista de selecionadas se estiver selecionada
      setSelecionadas(prev => prev.filter(cnpj => cnpj !== empresaParaDeletar.cnpj));
      
      carregarEmpresas(); // Recarregar lista
    } catch (error) {
      console.error('Erro ao deletar empresa:', error);
      const errorMsg = error.response?.data?.mensagem || 
                       error.response?.data?.erro || 
                       error.message || 
                       'Erro desconhecido ao deletar empresa';
      setMensagem(`Erro ao deletar empresa: ${errorMsg}`);
    } finally {
      setDeletandoEmpresa(false);
    }
  };

  // Função para alternar seleção de empresa
  const handleCheckbox = (cnpj) => {
    setSelecionadas((prev) =>
      prev.includes(cnpj) 
        ? prev.filter((item) => item !== cnpj) 
        : [...prev, cnpj]
    );
  };

  // Selecionar todas
  const selecionarTodas = () => {
    const empresasFiltradas = empresas.filter(emp =>
      Object.entries(filtros).every(([campo, valor]) =>
        emp[campo]?.toString().toLowerCase().includes(valor.toLowerCase())
      )
    );
    if (selecionadas.length === empresasFiltradas.length) {
      setSelecionadas([]);
    } else {
      setSelecionadas(empresasFiltradas.map(emp => emp.cnpj));
    }
  };

  // ================================
  // Gerar Relatórios Fiscais - UM POR VEZ
  // ================================
  const gerarRelatoriosFiscais = async () => {
    if (selecionadas.length === 0) {
      setMensagem('Selecione ao menos uma empresa');
      return;
    }

    setGerando(true);
    setProgresso('Iniciando processamento...');
    setMensagem('');
    setRelatoriosGerados([]);
    setEmpresasComErro([]);

    const accessToken = localStorage.getItem('access');
    const zip = new JSZip();
    const sucessos = [];
    const falhas = [];

    try {
      // Buscar dados das empresas selecionadas
      const empresasSelecionadas = empresas.filter(emp => 
        selecionadas.includes(emp.cnpj)
      );

      // Processar uma empresa por vez
      for (let i = 0; i < empresasSelecionadas.length; i++) {
        const empresa = empresasSelecionadas[i];
        const { cnpj, razaosocial } = empresa;
        
        setProgresso(`Processando ${i + 1}/${empresasSelecionadas.length}: ${razaosocial}`);

        try {
          const response = await axios.post(
            `${process.env.REACT_APP_API_BASE}/api/relatorio-fiscal/individual/`,
            { cnpj },
            {
              responseType: 'blob',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
              },
              timeout: 300000, // 5 minutos por empresa
            }
          );

          // Verificar se a resposta é um PDF
          const contentType = response.headers['content-type'];
          
          if (contentType && contentType.includes('application/pdf')) {
            // Sucesso - adicionar ao ZIP
            const nomeArquivo = response.headers['x-filename'] || 
                               `relatorio_fiscal_${cnpj}.pdf`;
            
            zip.file(nomeArquivo, response.data);
            sucessos.push(`${formatarCNPJ(cnpj)} - ${razaosocial}`);
            
            setRelatoriosGerados(prev => [...prev, {
              cnpj,
              razaosocial,
              status: 'sucesso',
              arquivo: nomeArquivo
            }]);
            
          } else {
            // Erro retornado como JSON
            const text = await response.data.text();
            const json = JSON.parse(text);
            throw new Error(json.mensagem || 'Erro desconhecido');
          }

        } catch (error) {
          console.error(`Erro para ${cnpj}:`, error);
          
          let errorMessage = 'Erro desconhecido';
          if (error.response?.data) {
            try {
              const text = await error.response.data.text();
              const json = JSON.parse(text);
              errorMessage = json.mensagem || 'Erro no servidor';
            } catch {
              errorMessage = error.message || 'Erro de rede';
            }
          } else {
            errorMessage = error.message || 'Erro de conexão';
          }
          
          falhas.push(`${formatarCNPJ(cnpj)} - ${razaosocial}: ${errorMessage}`);
          
          setEmpresasComErro(prev => [...prev, {
            cnpj,
            razaosocial,
            erro: errorMessage
          }]);
        }

        // Pausa entre requisições para não sobrecarregar
        if (i < empresasSelecionadas.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      // Criar relatório de execução
      const relatorioExecucao = `RELATÓRIO DE EXECUÇÃO - RELATÓRIOS FISCAIS
Data/Hora: ${new Date().toLocaleString('pt-BR')}
${'='.repeat(60)}

Total de empresas processadas: ${empresasSelecionadas.length}
Sucessos: ${sucessos.length}
Falhas: ${falhas.length}

${sucessos.length > 0 ? `SUCESSOS:\n${'-'.repeat(30)}\n${sucessos.join('\n')}\n\n` : ''}
${falhas.length > 0 ? `FALHAS:\n${'-'.repeat(30)}\n${falhas.join('\n')}` : ''}`;

      zip.file('relatorio_execucao.txt', relatorioExecucao);

      // Gerar e baixar ZIP
      if (sucessos.length > 0) {
        setProgresso('Criando arquivo ZIP...');
        
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
        const nomeArquivo = `relatorios_fiscais_${timestamp}.zip`;
        
        saveAs(zipBlob, nomeArquivo);
        
        setMensagem(`Processamento concluído! 
Sucessos: ${sucessos.length}
Falhas: ${falhas.length}
Arquivo baixado: ${nomeArquivo}`);
        
        setProgresso('Download concluído!');
        
        // Limpar seleções após sucesso
        setSelecionadas([]);
        
      } else {
        setMensagem('Nenhum relatório foi gerado com sucesso. Verifique os erros acima.');
      }

    } catch (error) {
      console.error('Erro geral:', error);
      setMensagem(`Erro inesperado: ${error.message}`);
    } finally {
      setGerando(false);
      setProgresso('');
    }
  };

  // Filtrar empresas
  const empresasFiltradas = empresas.filter(emp =>
    Object.entries(filtros).every(([campo, valor]) =>
      emp[campo]?.toString().toLowerCase().includes(valor.toLowerCase())
    )
  );

  return (
    <div className="relatorio-fiscal-page">
      <div className="container mt-5 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            <i className="fas fa-file-chart-line header-icon me-2"></i>
            <h2 className="titulo-pagina m-0">Relatórios Fiscais</h2>
          </div>
          <div>
            <button 
              className="btn btn-success me-2"
              onClick={() => setShowModal(true)}
              disabled={gerando}
            >
              <i className="fas fa-plus me-2"></i>
              Adicionar Empresa
            </button>
            <button 
              className="btn btn-secondary"
              onClick={carregarEmpresas}
              disabled={carregando || gerando}
            >
              <i className="fas fa-sync-alt me-2"></i>
              Atualizar
            </button>
          </div>
        </div>

        {/* Mensagens agora são exibidas como toast (react-toastify) */}

        {progresso && gerando && (
          <div className="alert alert-info">
            <div className="d-flex align-items-center">
              <div className="spinner-border spinner-border-sm me-3" role="status" aria-hidden="true"></div>
              <span>{progresso}</span>
            </div>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="alert alert-info mb-0">
            <i className="fas fa-info-circle me-2"></i>
            <strong>Novo Sistema:</strong>
            <ul className="mb-0 mt-2">
              <li>Processa uma empresa por vez</li>
              <li>Sem limite de quantidade</li>
              <li>Cria ZIP automaticamente no seu computador</li>
              <li>Mais estável e confiável</li>
            </ul>
          </div>

          <button 
            className="btn btn-primary px-4"
            onClick={gerarRelatoriosFiscais}
            disabled={gerando || selecionadas.length === 0}
          >
            {gerando ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Processando...
              </>
            ) : (
              <>
                <i className="fas fa-file-chart-line me-2"></i>
                Gerar Relatórios ({selecionadas.length})
              </>
            )}
          </button>
        </div>

        {selecionadas.length > 0 && (
          <div className="alert alert-success">
            <i className="fas fa-clock me-2"></i>
            <strong>Selecionadas: {selecionadas.length} empresa(s)</strong>
            <br />
            Tempo estimado: aproximadamente {Math.ceil(selecionadas.length * 1.5)} minutos
            <br />
            <small>Cada empresa é processada individualmente (mais estável)</small>
          </div>
        )}

        {/* Progresso em tempo real */}
        {gerando && (relatoriosGerados.length > 0 || empresasComErro.length > 0) && (
          <div className="alert alert-light">
            <h6>Progresso em tempo real:</h6>
            <div className="row">
              <div className="col-md-6">
                <strong className="text-success">Sucessos ({relatoriosGerados.length}):</strong>
                <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                  {relatoriosGerados.map((rel, idx) => (
                    <div key={idx} className="small text-success">
                      <i className="fas fa-check-circle text-success me-1"></i>{formatarCNPJ(rel.cnpj)} - {rel.razaosocial}
                    </div>
                  ))}
                </div>
              </div>
              <div className="col-md-6">
                <strong className="text-danger">Falhas ({empresasComErro.length}):</strong>
                <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                  {empresasComErro.map((err, idx) => (
                    <div key={idx} className="small text-danger">
                      <i className="fas fa-times-circle text-danger me-1"></i>{formatarCNPJ(err.cnpj)} - {err.razaosocial}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="table-responsive">
          {carregando ? (
            <div className="text-center mt-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
              <p>Carregando empresas...</p>
            </div>
          ) : (
            <table className="table table-bordered table-hover text-center align-middle fs-6">
              <thead className="table-light">
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      aria-label="Selecionar todas as empresas"
                      checked={selecionadas.length === empresasFiltradas.length && empresasFiltradas.length > 0}
                      onChange={selecionarTodas}
                      disabled={gerando}
                    />
                  </th>
                  <th>
                    Razão Social
                    <input
                      type="text"
                      className="form-control form-control-sm mt-1"
                      placeholder="Filtrar..."
                      value={filtros.razaosocial}
                      onChange={(e) => setFiltros(prev => ({ ...prev, razaosocial: e.target.value }))}
                      disabled={gerando}
                    />
                  </th>
                  <th>
                    CNPJ
                    <input
                      type="text"
                      className="form-control form-control-sm mt-1"
                      placeholder="Filtrar..."
                      value={filtros.cnpj}
                      onChange={(e) => setFiltros(prev => ({ ...prev, cnpj: e.target.value }))}
                      disabled={gerando}
                    />
                  </th>
                  <th width="100">Ações</th>
                </tr>
              </thead>
              <tbody>
                {empresasFiltradas.map((empresa) => (
                  <tr key={empresa.cnpj} className={selecionadas.includes(empresa.cnpj) ? 'table-primary' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selecionadas.includes(empresa.cnpj)}
                        onChange={() => handleCheckbox(empresa.cnpj)}
                        disabled={gerando}
                      />
                    </td>
                    <td className="text-start">{empresa.razaosocial}</td>
                    <td>{formatarCNPJ(empresa.cnpj)}</td>
                    <td>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => deletarEmpresa(empresa.cnpj, empresa.razaosocial)}
                        disabled={gerando}
                        title="Deletar empresa"
                        aria-label="Deletar empresa"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          
          {!carregando && empresasFiltradas.length === 0 && (
            <div className="text-center mt-4">
              <p className="text-muted">Nenhuma empresa encontrada.</p>
            </div>
          )}
        </div>

        {/* Resumo */}
        <div className="mt-3 p-3 bg-light rounded">
          <div className="row">
            <div className="col-md-3">
              <strong>Total:</strong> {empresas.length}
            </div>
            <div className="col-md-3">
              <strong>Filtradas:</strong> {empresasFiltradas.length}
            </div>
            <div className="col-md-3">
              <strong>Selecionadas:</strong> {selecionadas.length}
            </div>
            <div className="col-md-3">
              <strong>Tempo estimado:</strong> ~{Math.ceil(selecionadas.length * 1.5)} min
            </div>
          </div>
        </div>
      </div>

      {/* Modal para adicionar empresa */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title mb-0 fw-bold">
                  <i className="fas fa-plus me-2"></i>
                  Adicionar Nova Empresa
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowModal(false)}
                  disabled={salvandoEmpresa}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="razaosocial" className="form-label">Razão Social *</label>
                  <input
                    type="text"
                    className="form-control"
                    id="razaosocial"
                    value={novaEmpresa.razaosocial}
                    onChange={(e) => setNovaEmpresa(prev => ({ ...prev, razaosocial: e.target.value }))}
                    placeholder="Digite a razão social da empresa"
                    disabled={salvandoEmpresa}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="cnpj" className="form-label">CNPJ *</label>
                  <input
                    type="text"
                    className="form-control"
                    id="cnpj"
                    value={novaEmpresa.cnpj}
                    onChange={(e) => setNovaEmpresa(prev => ({ ...prev, cnpj: e.target.value }))}
                    placeholder="00.000.000/0000-00"
                    maxLength="18"
                    disabled={salvandoEmpresa}
                  />
                  <div className="form-text">Digite apenas os números ou com formatação</div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowModal(false)}
                  disabled={salvandoEmpresa}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="btn btn-success" 
                  onClick={adicionarEmpresa}
                  disabled={salvandoEmpresa || !novaEmpresa.razaosocial.trim() || !novaEmpresa.cnpj.trim()}
                >
                  {salvandoEmpresa ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Salvando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      Adicionar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação para deletar */}
      {showDeleteModal && empresaParaDeletar && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title mb-0 fw-bold text-danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Confirmar Exclusão
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deletandoEmpresa}
                ></button>
              </div>
              <div className="modal-body">
                <p>Tem certeza que deseja deletar a empresa:</p>
                <div className="alert alert-warning">
                  <strong>Razão Social:</strong> {empresaParaDeletar.razaosocial}<br />
                  <strong>CNPJ:</strong> {formatarCNPJ(empresaParaDeletar.cnpj)}
                </div>
                <p className="text-danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Esta ação não pode ser desfeita!
                </p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deletandoEmpresa}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={confirmarDelete}
                  disabled={deletandoEmpresa}
                >
                  {deletandoEmpresa ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Deletando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash me-2"></i>
                      Sim, Deletar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RelatorioFiscal;